import { MARKETING_LOCALES } from "../../../marketing-content/marketingLocales";
import { getMarketingTranslations } from "../../../marketing-content/marketingTranslations";
import { MarketingLanguageSelector } from "../../marketing-site/MarketingLanguageSelector";
import { MarketingLanguageSuggestion } from "../../marketing-site/MarketingLanguageSuggestion";
import React from "react";
import { Box, Button, Flex, Image, Link, VisuallyHidden } from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import JungleLogo from "../../../images/JungleLogo";
import { IconClose } from "../../../icons";
import { landing } from "../landingTokens";
import { trackLandingCallToActionClick } from "../handoff/appHandoff";
import { useAppUrl, useIsHydrated } from "../handoff/useAppUrl";
import { useLandingTheme } from "../LandingThemeContext";
import { useLandingLanguage } from "../LandingLanguageContext";
import { getLandingCopy, LandingLanguage } from "../landingCopy";
import { CONTAINER_WIDTH, PAGE_SIDE_PADDING } from "./themed/ThemedLayout";
import { AppStoreButtons, WEB_APP_CTA_CLASS_NAME } from "./AppStoreCallToAction";

function HamburgerMenuIcon({ color }: { color: string }) {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <path d="M4 7h20M4 14h20M4 21h20" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

// `language` is optional: left out, the nav follows the page's LandingLanguageProvider. The home page passes
// `showLanguageSelector={false}`: its URL owns the language, with the manual choice in its footer.
export default function LandingNav({ language: languageFromProp, showLanguageSelector = true }: { language?: LandingLanguage; showLanguageSelector?: boolean }) {
  const languageFromContext = useLandingLanguage();
  const language = languageFromProp ?? languageFromContext;
  const navLabels = getMarketingTranslations(language).nav;
  const navAccessibilityCopy = getLandingCopy(language).nav;
  // Login and Sign Up live in the app; the hrefs carry the visitor's attribution params across.
  const loginHref = useAppUrl(landing.loginUrl);
  const signUpHref = useAppUrl(landing.signUpUrl);
  const isHydrated = useIsHydrated();
  // The logo returns to whichever landing page is being viewed: the /testing preview, the Spanish home
  // page (/es) from Spanish pages, or the real one.
  const isTestingPreview = useLocation().pathname.startsWith("/testing");
  const homeLinkPath = isTestingPreview ? "/testing" : MARKETING_LOCALES[language].home;
  const theme = useLandingTheme();
  const isOriginal = theme.id === "original";
  const onDark = theme.isDark || theme.navOnDark;

  const linkColor = isOriginal ? landing.color.label : onDark ? "rgba(255, 255, 255, 0.85)" : theme.color.text;
  const iconColor = onDark ? "#FFFFFF" : "#4C4D4C";
  const menuBg = theme.navOnDark && !theme.isDark ? theme.color.accentDark : theme.color.pageBg;

  // The original keeps Framer's grey pill; themed versions make Sign Up the one filled action in the nav.
  const signUpButtonStyle = isOriginal
    ? {
        bg: landing.color.navButtonBg,
        color: landing.color.label,
        border: "none",
        boxShadow: "none",
        borderRadius: "8px",
        _hover: { bg: "#E4E7E5" },
      }
    : {
        bg: "transparent",
        color: onDark ? "#FFFFFF" : theme.color.heading,
        border: onDark ? "1.5px solid rgba(255, 255, 255, 0.45)" : `1.5px solid ${theme.color.heading}`,
        boxShadow: "none",
        borderRadius: theme.shape.buttonRadius,
        _hover: { bg: onDark ? "rgba(255, 255, 255, 0.1)" : theme.color.accentSoft },
      };

  const desktopSignUpButtonStyle = {
    fontFamily: isOriginal ? landing.font.nav : theme.font.body,
    fontSize: "16px",
    fontWeight: 600,
    h: isOriginal ? "40px" : "44px",
    px: isOriginal ? "16px" : "20px",
    transition: "background-color 0.2s ease, color 0.2s ease",
    ...signUpButtonStyle,
    _hover: { ...signUpButtonStyle._hover, textDecoration: "none" },
  };

  return (
    <Box as="header" data-marketing-nav-ready={isHydrated ? "" : undefined} position="relative" zIndex={10}>
      <Flex
        maxW={{ lg: isOriginal ? "950px" : CONTAINER_WIDTH }}
        mx="auto"
        px={isOriginal ? { base: "28px", md: "40px", lg: 0 } : PAGE_SIDE_PADDING}
        h={isOriginal ? "84px" : "96px"}
        align="center"
        justify="space-between"
      >
        <Box
          as={RouterLink}
          to={homeLinkPath}
          aria-label={navAccessibilityCopy.homeLinkAriaLabel}
          display="flex"
          alignItems="center"
        >
          {onDark ? (
            // The file's size (htmlWidth/htmlHeight) gives the logo its width before it loads, so the language
            // selector beside it does not move when it arrives.
            <Image src="/images/landing/footer-logo.png" alt="Jungle" htmlWidth={1321} htmlHeight={776} h={isOriginal ? "52px" : "60px"} w="auto" />
          ) : (
            <JungleLogo height={isOriginal ? 52 : 60} />
          )}
        </Box>

        {showLanguageSelector && <MarketingLanguageSelector language={language} />}

        {/* Desktop + tablet links */}
        <Flex
          display={{ base: "none", md: "flex" }}
          align="center"
          gap="28px"
          fontFamily={isOriginal ? landing.font.nav : theme.font.body}
        >
          <Link
            href={loginHref}
            onClick={() => trackLandingCallToActionClick("login")}
            display="inline-flex"
            alignItems="center"
            h="44px"
            lineHeight="1"
            fontSize="16px"
            fontWeight={500}
            color={linkColor}
            _hover={{ textDecoration: "none", color: onDark ? "#FFFFFF" : theme.color.accent }}
          >
            {navLabels.login}
          </Link>
          {/* Login stays on the web. Sign Up becomes the store link for phone visitors from a Meta ad; a phone
              held sideways is wide enough for this row. */}
          <Button
            as="a"
            href={signUpHref}
            className={WEB_APP_CTA_CLASS_NAME}
            onClick={() => trackLandingCallToActionClick("sign_up")}
            {...desktopSignUpButtonStyle}
          >
            {navLabels.signUp}
          </Button>
          <AppStoreButtons placement="nav" label="get_the_app" onlyAfterHydration {...desktopSignUpButtonStyle} />
        </Flex>

        {/* Native disclosure keeps the phone menu usable before hydration and
            when JavaScript is delayed. Its links are always in the static HTML. */}
        <Box
          as="details"
          data-marketing-mobile-menu
          display={{ base: "block", md: "none" }}
          sx={{
            "& > summary::-webkit-details-marker": { display: "none" },
            "&:not([open]) [data-menu-close]": { display: "none" },
            "&[open] [data-menu-open]": { display: "none" },
          }}
        >
          <Box
            as="summary"
            data-marketing-mobile-menu-toggle
            display="flex"
            alignItems="center"
            justifyContent="center"
            minW="44px"
            minH="44px"
            me="-8px"
            borderRadius="8px"
            cursor="pointer"
            listStyleType="none"
            _focusVisible={{ outline: "2px solid currentColor", outlineOffset: "2px" }}
            color={iconColor}
          >
            <Box as="span" data-menu-open>
              <VisuallyHidden>{navAccessibilityCopy.openMenuAriaLabel}</VisuallyHidden>
              <HamburgerMenuIcon color={iconColor} />
            </Box>
            <Box as="span" data-menu-close>
              <VisuallyHidden>{navAccessibilityCopy.closeMenuAriaLabel}</VisuallyHidden>
              <IconClose width={22} height={22} color={iconColor} />
            </Box>
          </Box>
          <Flex
            direction="column"
            position="absolute"
            top={isOriginal ? "84px" : "96px"}
            insetInlineStart={0}
            insetInlineEnd={0}
            bg={menuBg}
            borderBottom={`1px solid ${theme.color.border}`}
            px="28px"
            py="16px"
            gap="12px"
            fontFamily={theme.font.body}
          >
            <Link
              href={loginHref}
              onClick={() => trackLandingCallToActionClick("login")}
              fontSize="18px"
              fontWeight={500}
              color={linkColor}
              py="8px"
            >
              {navLabels.login}
            </Link>
            <Button
              as="a"
              href={signUpHref}
              className={WEB_APP_CTA_CLASS_NAME}
              onClick={() => trackLandingCallToActionClick("sign_up")}
              bg={theme.color.accent}
              color={theme.button.primaryColor}
              fontFamily={theme.font.body}
              fontSize="16px"
              fontWeight={600}
              h="48px"
              borderRadius={theme.shape.buttonRadius}
              _hover={{ bg: theme.color.accentHover, textDecoration: "none" }}
            >
              {navLabels.signUp}
            </Button>
            <AppStoreButtons
              placement="nav_menu"
              label="get_the_app"
              bg={theme.color.accent}
              color={theme.button.primaryColor}
              fontFamily={theme.font.body}
              fontSize="16px"
              fontWeight={600}
              h="48px"
              borderRadius={theme.shape.buttonRadius}
              _hover={{ bg: theme.color.accentHover, textDecoration: "none" }}
            />
          </Flex>
        </Box>
      </Flex>

      <MarketingLanguageSuggestion />
    </Box>
  );
}
